import {match, Pattern} from 'ts-pattern';
import {z} from 'zod';

import {isNil} from 'ramda';

import type {
  PricelistVersionHistoryItemResponseBody,
  PricelistVersionRequestBody,
} from '@dms/api/new-vehicle';

import {catalogueErrorCodes} from './catalogueUi';

const SERVER_ERROR = 500;

/** The RTK Query failures whose request may or may not have reached the server. */
const transportFailure = z.union([
  z.object({status: z.enum(['FETCH_ERROR', 'TIMEOUT_ERROR'])}),
  z.object({status: z.literal('PARSING_ERROR'), originalStatus: z.number()}),
  z.object({status: z.number()}),
]);

/**
 * A network error, or a 5xx without an error body (spec 7.5): the batch may have been committed
 * while its response was lost. Any other refusal says what the server did.
 */
export const isUnknownBatchOutcome = (error: unknown) => {
  const failure = transportFailure.safeParse(error).data;
  if (isNil(failure)) {
    return false;
  }
  return match(failure)
    .with({status: Pattern.union('FETCH_ERROR', 'TIMEOUT_ERROR')}, () => true)
    .with({status: 'PARSING_ERROR'}, (parsing) => parsing.originalStatus >= SERVER_ERROR)
    .with(
      {status: Pattern.number},
      (response) => response.status >= SERVER_ERROR && catalogueErrorCodes(error).length === 0
    )
    .exhaustive();
};

/**
 * Whether a batch with a pricelist version, whose response was lost, was written (spec 7.5): the
 * model year has the sent label, and the newest History entry has that label, the origin
 * `itemChange`, the current user and the sent note. The number of History changes is not compared:
 * the BE records its own rows per item type, and a count that differs would send a written batch
 * again. A failed read tells nothing: `'unknown'`, as sending the batch again could write it twice.
 * Accepted risk: the same user commits a batch with the same label and note from a second tab
 * meanwhile.
 */
export const readBackBatchOutcome = (options: {
  sent: PricelistVersionRequestBody;
  readLabel: () => Promise<string>;
  readNewestVersion: () => Promise<PricelistVersionHistoryItemResponseBody | undefined>;
  readCurrentUserId: () => Promise<string>;
}): Promise<'written' | 'notWritten' | 'unknown'> =>
  Promise.all([options.readLabel(), options.readNewestVersion(), options.readCurrentUserId()])
    .then(([label, newest, userId]) =>
      label === options.sent.label &&
      !isNil(newest) &&
      newest.versionLabel === options.sent.label &&
      newest.origin === 'itemChange' &&
      newest.changedBy === userId &&
      (newest.note ?? '') === (options.sent.note ?? '')
        ? ('written' as const)
        : ('notWritten' as const)
    )
    .catch(() => 'unknown' as const);
